import { Box, Chip, makeStyles, Tooltip } from "@material-ui/core"
import React, { useEffect, useState } from "react"
import { NavLink, useHistory, useParams } from "react-router-dom"
import pluralize from "pluralize"
import {
  ActionCard,
  AuthorTools,
  Icon,
  IconMessage,
  RowBox,
  TrainingModuleCreator,
  LoadingSpinner,
  SearchInput,
} from "../../components"
import { useMutationDeleteTrainingModule, useQueryTrainingModules } from "../../data"
import { toId } from "../../utils"
import { useAuth } from "../../services"

const useStyles = makeStyles((theme) => ({
  info: {
    fontSize: 14,
    color: theme.palette.text.light,
    marginBottom: theme.spacing(1),
  },
  rightChildren: {
    height: 40,
    overflow: "visible",
  },
  chip: {
    fontSize: 13,
    cursor: "pointer",
    color: theme.palette.text.light,
    "&:hover": {
      backgroundColor: theme.palette.shaded.darker,
    },
  },
}))

const Modules = () => {
  const classes = useStyles()
  const history = useHistory()
  const { hasFeature } = useAuth()
  const { id } = useParams()
  const { data, loading, refetch } = useQueryTrainingModules()
  const [deleteTrainingModule] = useMutationDeleteTrainingModule()

  const [edit, setEdit] = useState(null)
  const [searchText, setSearchText] = useState("")

  useEffect(() => {
    if (data && id && id !== "new") {
      setEdit(data.trainingModules.find((item) => toId(item) === id))
    } else if (!id && edit) {
      setEdit(null)
    }
  }, [data, edit, id])

  const handleFilter = (text) => {
    setSearchText(text.toLowerCase())
  }

  const handleEdit = (item) => {
    history.push(`/training/modules/${toId(item)}`)
  }
  const handleDelete = async (item) => {
    await deleteTrainingModule({ variables: { id: toId(item) } })
    refetch()
  }
  const handleClose = () => {
    history.push("/training/modules")
    refetch()
  }

  const isNew = id === "new"

  const hasFilter = data?.trainingModules.length > 5

  const hasTrainingAssessment = hasFeature("training_assessment")

  return (
    <>
      {(isNew || Boolean(edit)) && <TrainingModuleCreator edit={edit} onClose={handleClose} />}

      {hasFilter && <SearchInput placeholder="Filter modules" boxProps={{ pl: 0, pr: 0 }} onChange={handleFilter} />}

      {data && (
        <Box data-cy="Modules-list">
          {data.trainingModules
            .filter((item) => !searchText || item.name.toLowerCase().includes(searchText))
            .map((item) => (
              <ActionCard
                key={toId(item)}
                title={item.name}
                cursor="default"
                detail={item.knowledges.length ? pluralize("article", item.knowledges.length, true) : null}
                rightChildren={
                  <Box
                    mr="auto"
                    display="flex"
                    flexDirection="row"
                    alignSelf="center"
                    alignItems="center"
                    className={classes.rightChildren}
                  >
                    <AuthorTools
                      item={item}
                      subject="module"
                      admins={{ edit: "training_update_all", delete: "training_delete_all" }}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                    />
                  </Box>
                }
                footer={
                  Boolean(item.knowledges.length) && (
                    <RowBox flexWrap="wrap" style={{ rowGap: 10 }}>
                      {item.knowledges.map((knowledge, knowledgeIndex) => (
                        <RowBox key={toId(knowledge)} alignItems="center">
                          <Tooltip title="Go to article">
                            <Chip
                              component={NavLink}
                              to={`/knowledge/article/${toId(knowledge)}`}
                              label={knowledge.title}
                              className={classes.chip}
                            />
                          </Tooltip>
                          {knowledgeIndex < item.knowledges.length - 1 && <Icon name="right" />}
                        </RowBox>
                      ))}
                    </RowBox>
                  )
                }
              />
            ))}
        </Box>
      )}

      {!data && loading && (
        <Box display="flex" justifyContent="center">
          <LoadingSpinner size={60} />
        </Box>
      )}

      {data?.trainingModules.length === 0 && (
        <IconMessage
          name="empty-trainingmodules"
          maxWidth={500}
          text={
            <>
              {!hasTrainingAssessment && (
                <>
                  <Box mb={2}>
                    Modules are areas of training focus for your business. They link articles from your Knowledge Base.
                  </Box>
                  <Box mb={2}>
                    Courses then combine several modules, and are what you assign to your employees to complete as part
                    of their online training.
                  </Box>
                  <Box mb={2}>Create your first module using the Create button above to get started with training.</Box>
                </>
              )}
              {hasTrainingAssessment && (
                <>
                  <Box mb={2}>
                    Modules are areas of training focus for your business. They can reference articles from your
                    Knowledge Base. Modules can also be associated with trainers, your employees capable of training
                    other staff on these areas of training focus.
                  </Box>
                  <Box mb={2}>
                    Courses then combine several modules, and are what you assign to your employees to complete as part
                    of their online training.
                  </Box>
                  <Box mb={2}>
                    Modules can also be used in Assessment to record your employees competencies, and plan any further
                    training.
                  </Box>
                  <Box mb={2}>Create your first module using the Create button above to get started with training.</Box>
                </>
              )}
            </>
          }
        />
      )}
    </>
  )
}

export { Modules }
